// agentLoop.js — Loop agentic pra modelos LOCAIS (Ollama / LM Studio).
//
// Sprint v10.7: dá ao Code session a capacidade de AGIR na máquina via tool
// calling. Modelos cloud (Claude/GPT) usam o agent loop do Worker; modelos
// locais usam ESTE loop, que roda no renderer e executa tools via Electron IPC
// (sem passar pelo Worker — o datacenter não alcança localhost).
//
// Fluxo:
//   1. Chama o modelo com TOOL_DEFS
//   2. Se há content → emite evento 'assistant'
//   3. Se há tool_calls → executa cada um (com aprovação se permissions='ask')
//      e re-alimenta os resultados, repetindo até o modelo parar de chamar
//      tools OU atingir maxIterations.
//
// Formato de mensagem (OpenAI-compatible, aceito por Ollama /api/chat e
// LM Studio /v1/chat/completions):
//   { role:'assistant', content, tool_calls:[{ id, type:'function', function:{ name, arguments:"<json>" } }] }
//   { role:'tool', tool_call_id, content }

import { TOOL_DEFS, MUTATING_TOOLS, TERMINAL_TOOLS, SEND_TO_USER_DEF, describeToolCall, parsePresentPlan, runTool } from './agentTools.js';
import { repairToolCall, parseToolArgs } from './toolArgs.js';
import { evaluateToolGate } from './loop/toolGate.js';
import { pushImages, imageMessage } from './loop/imageAttach.js';
import { createBatchBuffer } from './loop/batchBuffer.js';
import { handleArgsError } from './loop/argsErrorGate.js';
import { blockerFallbackNotice, classifyBlocker } from './loop/blockerTaxonomy.js';
import { preToolHookGate, postToolHook, stopHook } from './loop/wire/hookLifecycle.js';
import { decideToolDispatch, resolveToolExclusion } from './loop/decideToolDispatch.js';
import { decideApprovalGate } from './loop/shipApproval.js';
import { autoReview as runAutoReview } from './autoReview.js';
import { createAutoReviewMetrics, recordDecision, summarize as summarizeAutoReview } from './autoReviewMetrics.js';
import { deliverableAudit } from './deliverableAudit.js';
// verify-loop-observability E2 — per-turn stage trace + RAG-usage scoring (pure).
import { createTurnTrace, markStage, summarizeTrace } from './turnTrace.js';
import { scoreRagInjection } from './ragQuality.js';
// E1 (self-improve-hardening) — AUTO_CORRECT: the oracle coordinates the verify
// loop (executable verifiers are the judge); Google triage is advisory + injected
// by the caller via cfg.autoCorrect.triage. Hard cap lives with the contract.
import { runOracle, previewConsoleVerifier, AUTO_CORRECT_CAP } from './oracle/oracle.js';
import { recordEdit } from './oracle/greenLedger.js';
import {
  looksLikeComplexTask,
  isConversationalTurnForKind,
  isBillingOrCreditsError,
  billingErrorNotice,
  // P4-T01 — the stall/plan/write/commit nudge predicates + their message constants
  // moved into ./loop/decideNudge.js (the pure nudge-gate). Only TOOL_FAILURE_NUDGE
  // stays here (still used directly by the missing-args dispatch path).
  TOOL_FAILURE_NUDGE,
  TOOL_BEFORE_CLAIM_NUDGE,
  PRESENT_PLAN_NUDGE,
  CONTEXT_FIRST_NUDGE,
  buildMissingArgsNudge,
} from './codeAgentMode.js';
import { makeIdempotencyKey, isRetryableError, resolveRetry, sleep } from './agentRetry.js';
// arch-fable5 A2 — honest-green for narration (pure auditor; advisory only).
import { makeClaimGates } from './honesty/auditProgressClaim.js';
import { readProgressAuditEnabled, readSendToUserEnabled, readDoneClaimBlockEnabled, readToolGuardrailEnabled, readGreenLedgerEnforced, readNativeAnthropicToolsEnabled } from './flags/sprintFlags.js';
import { autonomyGateStep } from './loop/wire/autonomyStep.js';
// decideNudge is now consumed internally by classifyToollessReply (FC-2).
import { planOracleStep } from './loop/oracleGate.js';
import { ranChecksGate, RAN_CHECKS_NUDGE } from './loop/wire/ranChecksGate.js';
import { buildLoopCheckpointer } from './loop/wire/checkpointWire.js';
import { applyToolEffects } from './loop/applyToolEffects.js';
import { identicalTripped, toolOutcomeStop, maxRepeatedToolError, ENV_REPAIR_NUDGE, buildRepeatInfo } from './loop/loopHealth.js';
import { iterationSteering } from './loop/iterationSteering.js';
import { loadLedgerSeed, persistLedger } from './greenLedgerPersist.js';
import { writeProgressFile, readFeatureSummary } from './progressWriter.js';
import { scoreDelivery, evidenceFromRun } from './deliveryScore.js';
import { mineFailureToEvalSet } from './evalMiner.js';
import { normalizeLoopToolCalls } from './loop/toolCallNormalize.js';
import { resolveFinishAction } from './loop/finishReason.js';
import { editOldToolResults } from './loop/contextEdit.js';
import { digestDroppedMessages } from './loop/convoDigest.js';
import { budgetExceeded } from './loop/runBudget.js';
import { triageFailure } from './loop/failureTriage.js';
import { markRunStart, clearRunMark } from './runInFlight.js';
import { gitHeadSha } from './gitHead.js';
import { prewarmParallelReads } from './loop/parallelReads.js';
import { initSpineState, applySpineEvent, reachedHonestGreen } from './loop/loopRunState.js';
import { classifyToollessReply } from './loop/classifyReply.js';
import { applyToollessReply } from './loop/applyToollessReply.js';
import { spinePolicy } from './loop/stepPolicy.js';
import { resultIsError, isMissingArgsError, isConfigToolError, contentSig, turnToolResultsForAudit, isChatWithoutRoot, rootlessChatToolResult } from './loop/spinePredicates.js';
// P0-T06 (software-to-9) — strict creative-build classifier for the generative fast path.
import { isGenerativeCreativeIntent } from './generativeIntent.js';

// Read/search tools that COUNT as "gathered context" before the first edit.
const CONTEXT_TOOLS = new Set(['semantic_search', 'search_brain', 'grep_search', 'read_file', 'list_files']);
// Write/edit tools whose first-action-without-context triggers the nudge.
const WRITE_TOOLS = new Set(['write_file', 'apply_edit']);
// E2-WS3-T02 — deltas do stream repassados ao onEvent (inclui o aviso de truncamento).
const FORWARDED_DELTAS = new Set(['token', 'reasoning', 'stream_truncated']);

// Converte tool_call normalizado { id, name, arguments } → formato API.
function toApiToolCall(tc) {
  return {
    id: tc.id,
    type: 'function',
    function: {
      name: tc.name,
      arguments: typeof tc.arguments === 'string' ? tc.arguments : JSON.stringify(tc.arguments ?? {}),
    },
  };
}

// Separa raciocínio inline <think>…</think> (DeepSeek-R1/MiniMax põem o
// pensamento DENTRO do content) do texto final. Sem isso o <think> vazava como
// texto cru e não virava ThinkingBlock. Puro + testável.
export function splitThink(text) {
  const raw = typeof text === 'string' ? text : '';
  if (!raw.includes('<think>')) return { reasoning: '', content: raw };
  let cleaned = '';
  let reasoning = '';
  let lastIndex = 0;
  const re = /<think>([\s\S]*?)<\/think>/gi;
  let m;
  while ((m = re.exec(raw)) !== null) {
    cleaned += raw.slice(lastIndex, m.index);
    reasoning += (reasoning ? '\n' : '') + m[1].trim();
    lastIndex = re.lastIndex;
  }
  cleaned += raw.slice(lastIndex);
  // <think> aberto sem </think> (resposta cortada): tudo após a tag é raciocínio.
  const open = cleaned.indexOf('<think>');
  if (open !== -1) {
    reasoning += (reasoning ? '\n' : '') + cleaned.slice(open + 7).trim();
    cleaned = cleaned.slice(0, open);
  }
  return { reasoning: reasoning.trim(), content: cleaned.trim() };
}

// HRN2 — context discipline. Long sessions (and big tool outputs re-sent every
// turn) blow the model's context. Bound the conversation BEFORE each send:
// keep the system messages, the last `maxMessages` non-system turns (drop the
// oldest — mirrors the 50-msg Worker cap), and truncate stale tool-result
// bodies so a giant earlier output doesn't dominate the re-sent prompt. Pure +
// testable.
export const MAX_CONVO_MESSAGES = 50;
export const MAX_TOOL_RESULT_CHARS = 4000;
// FASE-12 E2-T02 — resultado FRESCO (da iteração corrente, ainda não lido pelo
// modelo) tem teto próprio, bem mais generoso: cortar um stderr de 5k em 4000
// perdia exatamente a linha do erro que o modelo precisava pra corrigir.
export const MAX_FRESH_TOOL_RESULT_CHARS = 20000;
// PERF-2026-07: reduzido de 40 → 25. Com Ollama local, 40 iterations × 2min idle timeout
// = até 80min por task. 25 ainda permite tasks complexas mas evita que uma task travada
// segure recursos por tempo demais. Cloud (Claude/GPT) não usa este loop.
export const DEFAULT_MAX_ITERATIONS = 25;
// P0-T03 (software-to-9) — nudge ceiling. Each stall/first-action/write nudge is a
// FULL extra model call; a wedged small build can burn 20-60s on nudges alone.
// Cap the SHARED counter low so the loop gives up fast instead of paying for a
// long nudge gauntlet. Targets latency (the felt pain).
export const MAX_STALL_NUDGES = 1;
// P3-T01 — corrective nudge for a fabricated-green done (claims tests pass with no
// backing verifier). One-shot; if the model still claims green unbacked, the loop blocks.
export const UNBACKED_GREEN_NUDGE = [
  '[NUDGE] Você afirmou que os testes/build passaram, mas NENHUM verificador rodou neste turno',
  '(não há resultado de tool de teste/lint/build com sucesso).',
  'NÃO finalize com uma afirmação de sucesso não comprovada: RODE o teste/verificação de verdade',
  '(ex.: run_command com o comando de teste) e relate o resultado real — ou remova a afirmação de que passou.',
].join(' ');
// E4-T01 — a single LLM call must NOT hang the whole run forever. A wedged Ollama,
// a hung cloud connection, or a stuck adapter would otherwise pin the loop until a
// manual Stop. PERF-2026-07: reduzido de 120s → 90s. 90s é generoso pra um reasoning
// model pesado e curto o suficiente pra falhar limpo em Ollama instável.
export const LLM_CALL_TIMEOUT_MS = 90000;

// WSX (forense Pro Esporte) — teto ABSOLUTO (wall-clock) por chamada de LLM, IMUNE ao
// bump. O idle-timeout (acima) reseta a cada token; um stream que pinga `reasoning` pra
// sempre SEM concluir nunca fica ocioso → o idle nunca dispara → "Pensando…" eterno +
// buffer de reasoning crescendo (RAM). Este cap encerra UMA chamada que passa do teto
// independente de deltas, caindo no MESMO terminal 'llm_timeout' (surfaça o notice e
// deixa o finally limpar localRunning). 5 min cobre com folga uma resposta única
// normal (mesmo reasoning pesado); um stream CONTÍNUO que passe disso é tratado como
// travado e encerrado (o estado já mostrado é preservado). Em rota de modelo local
// lento, suba o teto por-run via options.llmCallMaxMs. (não-exportado: uso interno.)
// PERF-2026-07: reduzido de 10min → 5min. Com Ollama local instável, 10min × 2 retries
// × 40 iterations = tasks penduradas por horas. 5min ainda é generoso pra qualquer
// resposta única legítima.
const LLM_CALL_MAX_MS = 5 * 60 * 1000;

// E3-WS1-T01 — timeout numérico > 0 ou o fallback (helper fora do spine/ratchet).
function resolveTimeoutMs(v, fallback) {
  return Number(v) > 0 ? Number(v) : fallback;
}

// Race a single async call against a deadline. On timeout, fire `onTimeout` (the
// caller wires this to abort the in-flight network call, so it's actually
// cancelled — not just abandoned) and reject with a tagged timeout error. The
// timer is ALWAYS cleared on the normal/error path so no timer leaks and no late
// fire after the call already settled. Pure + unit-testable with fake timers.
//
// E3-WS1-T01 (FASE-11) — TTFT opcional: `ttftMs` aperta SÓ a PRIMEIRA janela
// (time-to-first-token); a partir do 1º bump as janelas voltam a `ms` (idle).
// Sem ttftMs o comportamento é byte-idêntico. IMPORTANTE: não há default < ms —
// chamadas NÃO-streaming (fallback nonStreamCall, chatFns locais sem stream)
// nunca emitem delta, e um TTFT hard curto abortaria respostas legítimas lentas.
// Aperte por-run via options.llmTtftTimeoutMs quando o caminho é 100% streaming.
/**
 * @param {() => Promise<any>} promiseFactory  starts the call
 * @param {number} ms  idle deadline in ms
 * @param {{ onTimeout?: () => void, bindBump?: (arm: () => void) => void, ttftMs?: number }} [opts]
 */
export function callWithTimeout(promiseFactory, ms, { onTimeout, bindBump = () => {}, ttftMs, maxMs } = {}) {
  let timer;
  let maxTimer;
  const timeout = new Promise((_resolve, reject) => {
    // #3/#8 — IDLE-timeout: arm() reinicia o deadline; o caller pluga o bump no stream → só o SILÊNCIO de `ms` aborta.
    let win = Number(ttftMs) > 0 ? Number(ttftMs) : ms; // 1ª janela = TTFT (se dado)
    const fireTimeout = (err) => {
      try { onTimeout?.(); } catch { /* abort best-effort */ }
      reject(err);
    };
    const arm = () => {
      clearTimeout(timer);
      const w = win;
      timer = setTimeout(() => {
        const err = new Error(w === ms ? `LLM call idle ${ms}ms` : `LLM sem primeiro token em ${w}ms (TTFT)`);
        err.isTimeout = true;
        fireTimeout(err);
      }, w);
      win = ms; // qualquer bump (1º token) muda para a janela de idle
    };
    arm();
    bindBump(arm);
    // WSX (forense Pro Esporte) — teto ABSOLUTO por chamada: NÃO é resetado pelo bump.
    // Um stream que pinga reasoning sem concluir nunca fica idle → só este cap o mata,
    // reusando a MESMA rejeição isTimeout (mesmo terminal llm_timeout do idle).
    const cap = Number(maxMs) > 0 ? Number(maxMs) : 0;
    if (cap > 0) {
      maxTimer = setTimeout(() => {
        const err = new Error(`LLM call wall-clock deadline ${cap}ms`);
        err.isTimeout = true;
        err.wallClockMs = cap;
        fireTimeout(err);
      }, cap);
    }
  });
  return Promise.race([Promise.resolve().then(promiseFactory), timeout])
    // Clear on every settle path (success, real error, OR timeout) so the timers
    // never leak and a resolved call can't trigger a late onTimeout.
    .finally(() => { clearTimeout(timer); clearTimeout(maxTimer); });
}

export function capConversation(messages, { maxMessages = MAX_CONVO_MESSAGES, maxToolResultChars = MAX_TOOL_RESULT_CHARS, maxFreshToolResultChars = MAX_FRESH_TOOL_RESULT_CHARS } = {}) {
  if (!Array.isArray(messages)) return [];
  const systems = messages.filter((m) => m && m.role === 'system');
  const rest = messages.filter((m) => m && m.role !== 'system');
  // Dente #5 — o trecho descartado vira DIGEST determinístico (fatos: tarefa,
  // arquivos, comandos) em vez de sumir num slice seco.
  let kept = rest;
  if (rest.length > maxMessages) {
    const digest = digestDroppedMessages(rest.slice(0, rest.length - maxMessages));
    kept = digest ? [{ role: 'user', content: digest }, ...rest.slice(rest.length - maxMessages)] : rest.slice(rest.length - maxMessages);
  }
  // FASE-12 E2-T02 — tool results DEPOIS da última msg do assistant são a rodada
  // CORRENTE (o modelo ainda não os leu): ganham o teto fresco. Os anteriores já
  // foram consumidos em iterações passadas → teto histórico de 4000 segue.
  let lastAssistant = -1;
  for (let i = kept.length - 1; i >= 0; i -= 1) {
    if (kept[i].role === 'assistant' || kept[i].role === 'agent') { lastAssistant = i; break; }
  }
  const capped = kept.map((m, i) => {
    if (m.role !== 'tool' || typeof m.content !== 'string') return m;
    const cap = i > lastAssistant ? maxFreshToolResultChars : maxToolResultChars;
    return m.content.length > cap ? { ...m, content: `${m.content.slice(0, cap)}\n…[truncated]` } : m;
  });
  return [...systems, ...capped];
}

function stringifyToolArgs(args) {
  if (typeof args === 'string') return args;
  try { return JSON.stringify(args ?? {}); } catch { return '{}'; }
}

// FASE-12 E3-T02 — resumo COMPACTO dos args pro flatten-retry. Serializar o arg
// inteiro reinjetava o content de um write_file grande no convo em cada flatten
// (o histórico inflava a cada retry → estourava o contexto). Aqui cada VALOR
// string longo é clipado a ~200 chars; a estrutura (chaves) fica intacta. Só o
// caminho de flatten usa isto — a assinatura do repeat-guard segue exata.
const FLATTEN_ARG_MAX = 200;
function summarizeToolArgs(args) {
  let obj = args;
  if (typeof args === 'string') {
    const s = args.trim();
    try { obj = JSON.parse(s); } catch { return s.length > FLATTEN_ARG_MAX ? `${s.slice(0, FLATTEN_ARG_MAX)}…` : s; }
  }
  if (!obj || typeof obj !== 'object') return stringifyToolArgs(args);
  const clipped = {};
  for (const [k, v] of Object.entries(obj)) {
    clipped[k] = typeof v === 'string' && v.length > FLATTEN_ARG_MAX ? `${v.slice(0, FLATTEN_ARG_MAX)}…[+${v.length - FLATTEN_ARG_MAX} chars]` : v;
  }
  try { return JSON.stringify(clipped); } catch { return '{}'; }
}

function describeApiToolCall(tc = {}) {
  const name = tc.function?.name || tc.name || 'tool';
  const args = tc.function?.arguments ?? tc.arguments ?? tc.input ?? {};
  return `${name}(${summarizeToolArgs(args)})`;
}

// Janelas de leitura que o repeat-guard deve IGNORAR: re-ler o MESMO arquivo com
// offset/limit diferente NÃO é progresso — é o loop "re-lendo em janelas pequenas"
// (dogfood 2026-07-17). Sem strip, cada janela vira assinatura nova e o guard nunca
// dispara (o run só morria lá na frente por autocorrect_cap). Cobre read_file do
// Apolo E os read_file de MCP (desktopcommander etc.).
const READ_WINDOW_KEYS = ['offset', 'limit', 'length', 'head', 'tail', 'lines', 'start', 'end', 'startLine', 'endLine', 'maxLines'];
function isReadTool(name) {
  return /(^|_|:)read_?(file|multiple_files)$/i.test(String(name || ''));
}
function normalizedToolSignature(name, args) {
  let a = args;
  if (isReadTool(name)) {
    let obj = a;
    if (typeof a === 'string') { try { obj = JSON.parse(a); } catch { obj = null; } }
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      const stripped = { ...obj };
      for (const k of READ_WINDOW_KEYS) delete stripped[k];
      a = stripped;
    }
  }
  return `${name}:${stringifyToolArgs(a)}`;
}

// FASE-12 E2-T01 — apply_edit É escrita: sem isto um turno só-de-edits pulava o
// aparato honest-green inteiro (done-gate, oracle, deliverableAudit, commit-nudge,
// greenLedger.recordEdit). content = new_string (fragmento) com partial:true — o
// deliverableAudit pula broken-link (inverificável num fragmento) e mantém o resto.
function wroteFileEffect(name, path, args) {
  if (!path) return null;
  if (name === 'write_file') return { path, content: String(args?.content ?? '') };
  if (name === 'apply_edit') return { path, content: String(args?.new_string ?? ''), partial: true };
  return null;
}

// P4-T05 (software-to-9) — PURE classification of a COMPLETED tool call into the
// loop-state effects it implies. Extracted from the 900-LOC runLocalAgentLoopInner
// hot path (post-execution tracking) so the effect rules live in ONE testable place
// and the loop just APPLIES the returned descriptor. `errored` is the caller's
// isToolError(result) (a run-local closure); passing it keeps this module-scope.
// Behavior-identical to the inline block it replaces (Feathers: characterization
// tests stay green).
export function deriveToolEffects(name, args, result, errored) {
  const path = String(args?.path || args?.filePath || args?.file || '');
  const eff = {
    gatheredContext: CONTEXT_TOOLS.has(name) && !errored,
    ragChunk: null,
    retrieveStage: null, // 'ok' | 'error' for search_brain/semantic_search
    toolErrorStage: false, // a non-retrieval tool that errored
    wroteFile: null, // { path, content }
    knownPath: null,
    committed: false,
  };
  if (name === 'search_brain' || name === 'semantic_search') {
    if (!errored && typeof result === 'string') eff.ragChunk = result;
    eff.retrieveStage = errored ? 'error' : 'ok';
  } else if (errored) {
    eff.toolErrorStage = true;
  }
  if (!errored) eff.wroteFile = wroteFileEffect(name, path, args);
  if ((name === 'read_file' || name === 'apply_edit') && !errored && path) eff.knownPath = path;
  if (name === 'run_command' && /\bgit\s+commit\b/.test(String(args?.command || args?.cmd || ''))) eff.committed = true;
  return eff;
}

// Some OpenAI-compatible providers accept tool calls in the first turn but reject
// the follow-up history containing `assistant.tool_calls` + `role: tool`.
// Flattening keeps the observations available while avoiding provider-specific
// tool-result schemas; tools remain enabled for the next model response.
export function flattenToolHistoryForRetry(messages, { maxToolResultChars = MAX_TOOL_RESULT_CHARS } = {}) {
  if (!Array.isArray(messages)) return [];
  const out = [];
  for (const m of messages) {
    if (!m || typeof m !== 'object') continue;
    if (m.role === 'system' || m.role === 'user') {
      // Preserve multimodal content (image_url blocks) — stringifying it would
      // turn the image into "[object Object]" and lose the attachment.
      const content = Array.isArray(m.content) ? m.content : String(m.content ?? '');
      out.push({ role: m.role, content });
      continue;
    }
    if (m.role === 'assistant' || m.role === 'agent') {
      const content = String(m.content ?? '').trim();
      if (content) out.push({ role: 'assistant', content });
      const calls = Array.isArray(m.tool_calls) ? m.tool_calls : [];
      if (calls.length > 0) {
        out.push({
          role: 'assistant',
          content: `Usei ferramentas: ${calls.map(describeApiToolCall).join('; ')}`,
        });
      }
      continue;
    }
    if (m.role === 'tool') {
      const body = String(m.content ?? '');
      const clipped = body.length > maxToolResultChars
        ? `${body.slice(0, maxToolResultChars)}\n...[truncated]`
        : body;
      out.push({
        role: 'user',
        content: [
          `Resultado da ferramenta${m.tool_call_id ? ` (${m.tool_call_id})` : ''}:`,
          clipped || '(sem saída)',
          '',
          'Continue a tarefa a partir deste resultado. Se precisar de mais dados, chame novas ferramentas.',
        ].join('\n'),
      });
    }
  }
  return out;
}

// HRN2-T03 — recognize a context-overflow error so it surfaces as a clear
// terminal notice instead of being swallowed as an empty "ok".
export function isContextOverflow(msg) {
  const s = String(msg || '').toLowerCase();
  // live-verify E3 (dogfood 07-14): "exceeded model token limit" é o fraseio do Kimi/Moonshot —
  // sem ele o overflow real virava model_error e o retry-com-compactação (T02) nunca disparava.
  return /context length|context window|too many tokens|maximum context|maximum.*tokens|reduce the length|prompt is too long|input is too long|413|payload too large|context_length_exceeded|exceeded model token limit/.test(s);
}

// Aviso terminal pra UI baseado no stopReason — o usuário SEMPRE sabe COMO o
// turno acabou (nunca "limbo do silêncio"). 'done'/'aborted' não precisam (o
// 'aborted' já tem a msg do Stop). Puro + testável.
function terminalNoticeBase(result) {
  const stop = result && result.stopReason;
  if (stop === 'max_iterations') {
    if (result.autoContinuing) return null;
    const max = result.maxIterations ?? result.iterations;
    const rounds = result.autoContinueRounds != null ? ` após ${result.autoContinueRounds + 1} rodadas` : '';
    return {
      role: 'system',
      content: `⏹️ Esgotei o orçamento de ${max} passos por rodada${rounds}. Diga "continue" ou descreva o próximo passo.`,
    };
  }
  // FASE-12 E3-T01/T03 — stall genérico e repeat-loop também saem do limbo do
  // silêncio (o stall_gave_up já tem a sua própria mensagem, mais específica, abaixo).
  if (stop === 'stall') {
    return {
      role: 'system',
      content: '⏹️ O modelo estagnou (plano/resumo em texto sem agir). O estado atual está preservado — diga "continue" para retomar ou refine a tarefa.',
    };
  }
  if (stop === 'tool_repeat_loop') {
    // T3+T4 — a notícia carrega repeatInfo { tool, args, failing }. Quando a
    // ferramenta repetida estava FALHANDO (dependência/MCP fora do ar, rede), o
    // problema NÃO é a tarefa: aponta pra ferramenta. Caso normal: nomeia a
    // ferramenta+args e sugere trocar de abordagem — nada de "3+ vezes seguidas".
    const info = result && result.repeatInfo;
    const tool = info?.tool ? `\`${info.tool}\`` : 'a mesma ferramenta';
    const args = info?.args ? ` (args: ${info.args})` : '';
    if (info?.failing) {
      return {
        role: 'system',
        content: `⚠️ A ferramenta ${tool} falhou repetidamente e entrou em loop de re-tentativa${args}. Provavelmente está indisponível — um servidor/MCP fora do ar ou erro de rede. Verifique a ferramenta/servidor e tente de novo; não é a sua tarefa que precisa mudar.`,
      };
    }
    return {
      role: 'system',
      content: `⏹️ Parei: ${tool}${args} foi repetida sem novo progresso. Aproveite o resultado que já veio, troque de ferramenta/argumento, ou conclua com o que já tem.`,
    };
  }
  if (stop === 'no_root') {
    return {
      role: 'system',
      content: '⚠️ Esta sessão não tem worktree/diretório local. Crie uma sessão Code com pasta do projeto antes de usar o agente.',
    };
  }
  if (stop === 'tool_failure_spiral') {
    return {
      role: 'system',
      content: '⚠️ Ferramentas falharam repetidamente (grep/list sem argumentos ou worktree inválido). Verifique a pasta do projeto da sessão e tente de novo.',
    };
  }
  if (stop === 'stall_gave_up') {
    return {
      role: 'system',
      content: '⚠️ O modelo respondeu com plano/texto sem executar ferramentas. Reformule a tarefa ou troque de modelo — o agente precisa de tool calling funcional.',
    };
  }
  if (stop === 'context_overflow') {
    return {
      role: 'system',
      content: '⚠️ A conversa ficou grande demais pro modelo (limite de contexto estourado). Resuma o que importa ou abra uma nova sessão pra continuar.',
    };
  }
  if (stop === 'autocorrect_cap') {
    const n = result?.autoCorrect?.attempts ?? 2;
    return {
      role: 'system',
      content: `⚠️ A auto-correção atingiu o limite (${n} tentativa(s)) e os verificadores continuam vermelhos. Revise o erro do preview/console e tente um fix manual ou troque de abordagem.`,
    };
  }
  if (stop === 'unbacked_green_claim') {
    return {
      role: 'system',
      content: '⚠️ O agente afirmou que os testes/build passaram sem nenhum verificador ter rodado — a conclusão foi bloqueada por ser uma alegação não comprovada. Rode a verificação de verdade ou reformule a tarefa.',
    };
  }
  if (stop === 'unbacked_write_claim') {
    return {
      role: 'system',
      content: '⚠️ O agente afirmou ter criado/editado um arquivo mas nenhuma ferramenta de escrita rodou (nem após o aviso) — a conclusão foi bloqueada por ser uma alegação não comprovada. Faça a edição de verdade ou retire o claim.',
    };
  }
  if (stop === 'llm_timeout') {
    // WSX — teto ABSOLUTO (wall-clock) estourou: o modelo ficou gerando sem concluir.
    if (result?.wallClockMs) {
      const min = Math.max(1, Math.round(result.wallClockMs / 60000));
      return {
        role: 'system',
        content: `⚠️ O modelo ficou ${min} min gerando sem concluir e foi encerrado por segurança (timeout). O estado atual está preservado — tente de novo, reduza o escopo ou troque de modelo.`,
      };
    }
    const ms = result?.llmCallTimeoutMs ?? LLM_CALL_TIMEOUT_MS;
    const seconds = Math.round(ms / 1000);
    return {
      role: 'system',
      content: `⚠️ O modelo não respondeu em ${seconds}s (timeout). Tente de novo ou troque de modelo.`,
    };
  }
  if (stop === 'tools_done_followup_failed') {
    const error = result?.error ? String(result.error) : '';
    if (isBillingOrCreditsError(error)) {
      return { role: 'system', content: billingErrorNotice(error) };
    }
    const suffix = error ? ` Erro: ${error}` : '';
    return {
      role: 'system',
      content: `⚠️ Fiz as ações, mas o modelo não encadeou o próximo passo (follow-up falhou).${suffix}`,
    };
  }
  if (stop === 'model_error' && isBillingOrCreditsError(result?.error)) {
    return { role: 'system', content: billingErrorNotice(result.error) };
  }
  if (stop === 'model_error') {
    const err = result?.error ? String(result.error) : 'modelo não respondeu';
    return { role: 'system', content: `⚠️ Erro do modelo: ${err}` };
  }
  // DeerFlow borrow #1 — stop sem mensagem específica NÃO termina mais em silêncio:
  // a taxonomia classifica (continue/ask_user/failed/…) e emite uma notícia TIPADA.
  // done/plan_*/aborted seguem silenciosos (blockerFallbackNotice devolve null).
  return blockerFallbackNotice(result);
}

// DeerFlow #1 follow-up — TODA notícia terminal (não só o fallback) carrega o tipo do
// blocker em metadata: o feed troca "Diga 'continue'" por um BOTÃO (BlockerActions).
export function terminalNotice(result) {
  const notice = terminalNoticeBase(result);
  if (!notice || notice.metadata) return notice;
  const { type } = classifyBlocker(result);
  if (type === 'done' || type === 'cancelled') return notice;
  return {
    ...notice,
    metadata: { kind: 'turn_blocker', blocker: type, stopReason: result?.stopReason || '' },
  };
}

/**
 * Roda o loop agentic local. Emite eventos via onEvent — não persiste nada
 * (o caller decide o que salvar/mostrar).
 *
 * @param {object} cfg
 * @param {Function} cfg.chatFn      window.apolo.ollama.chat | lmstudio.chat
 * @param {string}   cfg.baseUrl
 * @param {string}   cfg.model
 * @param {object}   [cfg.options]   { temperature, num_predict }
 * @param {string}   cfg.root        diretório da sessão (cwd das tools)
 * @param {Array}    cfg.messages    histórico inicial [{ role, content }]
 * @param {'ask'|'ignore'} cfg.permissionsMode
 * @param {number}   [cfg.maxIterations=40]
 * @param {(desc:string)=>Promise<boolean>} cfg.requestApproval
 * @param {(evt: import('./eventTypes.js').LoopEvent) => void} cfg.onEvent
 *   P2-T01 (code-tab-lift) — the loop's emit contract IS the LoopEvent discriminated
 *   union (eventTypes.js). Every onEvent({ type, … }) below is one of its variants;
 *   a new emit type must be added to the union, where summarizeEvent's exhaustive
 *   switch then forces it to be handled (`npm run tsc:fe-contract` is the gate).
 * @param {string}   [cfg.taskText]  user task (for auto-repairing empty tool args)
 * @param {import('./brandedIds.js').SessionId} [cfg.sessionId] code session id (semantic_search + index)
 *   P2-T02 (code-tab-lift) — branded SessionId at the seam; the caller constructs it
 *   with asSessionId(...) at the boundary so a UserId can't flow into this slot.
 * @param {Array<{def:object, execute:(args:object, ctx:object)=>Promise<string>, mutating?:boolean}>} [cfg.extraTools]
 *                   caller-injected tools (e.g. Hermes radar_emit). Their defs are
 *                   appended to TOOL_DEFS for the LLM call and their names dispatch
 *                   to the injected execute. `mutating: true` opts into the 'ask'
 *                   approval gate. Absent/empty ⇒ the loop behaves exactly as before.
 * @param {boolean}  [cfg.readOnly=false] analysis mode: MUTATING_TOOLS (and any
 *                   extra tool flagged `mutating`) are (a) removed from the tool
 *                   defs sent to the LLM and (b) hard-denied at dispatch if the
 *                   model calls one anyway (defense-in-depth). Default false ⇒
 *                   identical to prior behavior. Used by Team Mode's read-only
 *                   worker option so analysis runs cannot write/exec.
 * @returns {Promise<{ ok, finalText, iterations, stopReason }>}
 */
// E2-WS2-T01 — accumulate per-call usage so the local turn can be costed. Sums
// both OpenAI-compat (prompt/completion) and Anthropic (input/output) shapes.
// Pure mutation on the passed accumulator; never throws.
function addUsage(acc, reply) {
  const u = reply && typeof reply === 'object' ? reply.usage : null;
  if (!u || typeof u !== 'object') return;
  acc.input_tokens += Number(u.input_tokens ?? u.prompt_tokens ?? 0) || 0;
  acc.output_tokens += Number(u.output_tokens ?? u.completion_tokens ?? 0) || 0;
  // Carry an explicit OpenRouter-reported cost when a provider streams one.
  const cost = Number(u.cost ?? u.total_cost);
  if (Number.isFinite(cost) && cost >= 0) acc.cost = (acc.cost || 0) + cost;
}

/**
 * Roda o loop agentic local. Thin wrapper sobre o motor real (`runLocalAgentLoopInner`)
 * que ACUMULA o `usage` de TODAS as chamadas LLM do turno e o anexa ao resultado —
 * sem ter que tocar os ~20 returns internos. O consumidor (SessionWorkspace) lê
 * `result.usage` pra logar custo do turno LOCAL (E2-WS2-T01). Assinatura/eventos
 * idênticos; o único campo novo no retorno é `usage`.
 */
export async function runLocalAgentLoop(cfg) {
  const usage = { input_tokens: 0, output_tokens: 0 };
  const autoReviewMetrics = createAutoReviewMetrics();
  // E4-T04 — o edit-vs-green ledger sobrevive entre runs: semeia do snapshot
  // persistido e re-persiste ao fim (aqui no wrapper — fora do dp-loc do spine).
  let spine = null;
  markRunStart(cfg?.sessionId, cfg?.taskText); // Wave-J resume-not-restart — órfão vira aviso+retomada no boot
  const result = await runLocalAgentLoopInner({ ...cfg, ...loadLedgerSeed(cfg?.sessionId), spineSink: (s) => { spine = s; } }, usage, autoReviewMetrics);
  clearRunMark(cfg?.sessionId); // fechou (bem ou mal) = não é órfão
  persistLedger(cfg?.sessionId, spine);
  const deliveryScore = await emitPostRunSignals(cfg, spine, result); // Waves A/C/E/I/J — progress+eval+score+triagem
  const headSha = await gitHeadSha(cfg?.root); // Dente #4 — SHA de verificação viaja na telemetria
  // S4 (E4-WS1-T04) — additive: per-turn auto-review telemetry (counts only, no PII).
  return { ...result, usage, autoReview: summarizeAutoReview(autoReviewMetrics), deliveryScore, headSha };
}

// Cauda pós-run do wrapper (extraída: o wrapper cruzava o teto de complexity).
// Ordem preservada; TUDO best-effort — sinais nunca derrubam o retorno do run.
async function emitPostRunSignals(cfg, spine, result) {
  const emit = (evt) => { try { cfg?.onEvent?.(evt); } catch { /* telemetria nunca quebra o run */ } };
  // Wave-A — run que editou deixa registro mecânico em .apolo/progress.md (nunca lança).
  await writeProgressFile({ root: cfg?.root, taskText: cfg?.taskText, spine, stopReason: result?.stopReason });
  await mineFailureToEvalSet({ root: cfg?.root, taskText: cfg?.taskText, result }); // E15-T07 — falha vira caso no eval set
  // Wave-C — score 0-100 evidência-only; score null = nada mensurável.
  const deliveryScore = scoreDelivery({ ...evidenceFromRun(spine, result), features: await readFeatureSummary(cfg?.root) });
  const editedFiles = [...new Set((spine?.writtenFiles || []).map((f) => f?.path).filter(Boolean))]; // D-10v2
  if (deliveryScore.score != null) emit({ type: 'delivery_score', ...deliveryScore, files: editedFiles });
  const repeated = maxRepeatedToolError(spine); // D-11 pleno — erro repetido mesmo em run verde
  if (repeated) emit({ type: 'repeated_tool_error', ...repeated });
  const triage = triageFailure(result); // Wave-J — classify→ação (needs_human vira ping real)
  if (triage) emit({ type: 'run_triage', ...triage });
  return deliveryScore;
}

async function runLocalAgentLoopInner(cfg, usageTotals, autoReviewMetrics = createAutoReviewMetrics()) {
  const {
    chatFn, baseUrl, model, options = {}, root,
    messages, permissionsMode = 'ask', maxIterations = DEFAULT_MAX_ITERATIONS,
    requestApproval, onEvent = () => {}, signal, taskText = '', sessionId,
    extraTools = [],
    excludeTools = [], // fix A — tools retiradas DESTE run (DAG worker exclui update_todo); ver resolveToolExclusion

    // A7 scoping (E5) — headless/Hermes runs pass allowSendToUser:true so the verbatim-delivery tool
    // is exposed THERE without exposing it in normal Code/Chat (where the global flag stays default OFF).
    allowSendToUser = false,
    readOnly = false, maxCostUsd, maxTokensOut, // Wave-J — hard-stop de orçamento (undefined = defaults do runBudget)
    planMode = false, // plan-mode-renders — turno de proposta de plano (nudge present_plan)
    toolPolicy = null, // E5 — deny-list granular por-tool/por-path (null = sem policy)
    autoReview = null, // S4 — { enabled, policy, opts } pre-tool classifier (null = off, retrocompatible)
    // E1 (self-improve-hardening) — optional AUTO_CORRECT injection:
    // { verifiers?: {console?,types?,lint?,smoke?,build?}, triage?: fn }. Absent ⇒
    // the loop builds only the live-preview console verifier (when a window exists).
    autoCorrect = null,
    // Sprint tasking-cursor-ide P3-T06 — Task DAG threading. The current
    // task id (when Task Mode is active) is surfaced on telemetry and is
    // the hook P5-T03 will use for runOracleForTask(taskId, …). Absent ⇒
    // unchanged flat-todo behavior. Pure pass-through here; no logic
    // change on the agentLoop itself.
    currentTaskId = null,
    // P0-T06 (software-to-9) — one-shot generative fast mode. OPT-IN flag from the
    // caller (default OFF). Even when ON it ONLY applies to clearly-creative build
    // intent — the engine re-confirms via isGenerativeCreativeIntent so a caller
    // can never fast-path real code work (DECISIONS Q7). Off → byte-identical.
    generativeFastMode = false,
    sessionKind = null, // F13-E7-T06 — 'chat'|'code'|null: em chat, conversa é o default
  } = cfg;
  // S4 (E4-WS1-T01) — auto-review is OPT-IN: active only when a config with
  // enabled=true is passed. Off → byte-identical to pre-S4 behavior.
  const autoReviewActive = !!(autoReview && autoReview.enabled);
  // P0-T06 — double-gated: caller flag AND engine-confirmed creative intent. This is
  // the governance-relax boundary — conservative (default GOVERNED). When true, the
  // AUTO_CORRECT RED→re-run verifier loop is skipped (creative drafts shouldn't pay
  // the code-verify gauntlet); autonomous/code turns keep full governance.
  const fastGenerative = generativeFastMode === true && isGenerativeCreativeIntent(taskText);

  // Caller-injected tools (opt-in seam). Malformed entries are dropped — a bad
  // injection must never break the shared engine. With no valid extras the
  // LLM call sends TOOL_DEFS untouched (same array identity, zero overhead).
  const extraByName = new Map(
    (Array.isArray(extraTools) ? extraTools : [])
      .filter((t) => t?.def?.function?.name && typeof t.execute === 'function')
      .map((t) => [t.def.function.name, t]),
  );
  // read-only (analysis) mode: drop MUTATING_TOOLS from the defs the LLM sees so
  // it never even tries to write/exec. Extras flagged `mutating` drop too. When
  // readOnly is false AND there are no extras, allToolDefs IS TOOL_DEFS (same
  // identity, zero overhead) — the default path is byte-identical to before.
  const baseToolDefs = readOnly
    ? TOOL_DEFS.filter((d) => !MUTATING_TOOLS.has(d?.function?.name))
    : TOOL_DEFS;
  const extraDefList = [...extraByName.values()]
    .filter((t) => !(readOnly && t.mutating))
    .map((t) => t.def);
  const { defs: baseAllToolDefs, excluded: excludedToolNames } = resolveToolExclusion(extraDefList.length ? [...baseToolDefs, ...extraDefList] : baseToolDefs, excludeTools); // fix A
  // arch-fable5 A7 — expose send_to_user ONLY when the flag is ON (default OFF == byte-identical
  // tool set, same array identity). Read-only delivery tool, so it survives readOnly mode too.
  // A7 (E5) — exposed by the global flag OR when the run is headless/Hermes (allowSendToUser).
  const sendToUserOn = readSendToUserEnabled() || allowSendToUser === true;
  const allToolDefs = sendToUserOn ? [...baseAllToolDefs, SEND_TO_USER_DEF] : baseAllToolDefs;

  // E4-T01 — per-LLM-call timeout. Overridable via options for tests; defaults to
  // the 2-min guard. A single hung chatFn call must NOT pin the run forever.
  // E3-WS1-T01 — TTFT opcional (sem default: caminhos não-streaming nunca emitem delta).
  const llmCallTimeoutMs = resolveTimeoutMs(options.llmCallTimeoutMs, LLM_CALL_TIMEOUT_MS);
  const llmTtftTimeoutMs = resolveTimeoutMs(options.llmTtftTimeoutMs, undefined);

  if (typeof chatFn !== 'function') {
    onEvent({ type: 'error', error: 'chatFn indisponível (IPC local)' });
    return { ok: false, finalText: '', iterations: 0, stopReason: 'no_chat_fn' };
  }

  // F13-E7-T09 — chat SEM pasta roda (FS tools viram hint); Code exige root.
  const chatWithoutRoot = isChatWithoutRoot(sessionKind, root);
  if (!chatWithoutRoot && (!root || !String(root).trim())) {
    onEvent({ type: 'error', error: 'Sessão sem worktree/diretório local' });
    onEvent({ type: 'loop_stop', stopReason: 'no_root', iterations: 0 });
    return { ok: false, finalText: '', iterations: 0, stopReason: 'no_root', maxIterations };
  }

  // FC-1: 28 mutable accumulators folded into one state object. applySpineEvent is the only mutation path.
  const s = initSpineState({ messages, turnTrace: createTurnTrace(), sessionId, greenLedgerSeq: cfg.greenLedgerSeq, greenLedgerFiles: cfg.greenLedgerFiles });
  if (typeof cfg.spineSink === 'function') cfg.spineSink(s); // E4-T04 — o wrapper persiste o ledger no fim
  const progressAuditOn = readProgressAuditEnabled();
  const doneClaimBlockOn = readDoneClaimBlockEnabled();
  const _w = /** @type {any} */ (typeof window !== 'undefined' ? window : {});
  const checkpointer = buildLoopCheckpointer(root, _w.apolo?.shell?.exec
    ? (async ({ command, cwd }) => _w.apolo.shell.exec({ command, cwd: cwd || root })) : null);
  // A2 — tool result is ERROR on IPC failure OR non-zero exit. Pure predicates in loop/spinePredicates.js.
  const getTurnToolResults = () => turnToolResultsForAudit(s.convo, s.toolNameById, s.toolArgsById);
  const { auditAndFlag, claimsUnbackedGreen, claimsUnbackedWrite } = makeClaimGates({ getTurnToolResults, onEvent, progressAuditOn }); // E14-T01
  const isComplexTask = looksLikeComplexTask(taskText);
  const isConversationalTask = isConversationalTurnForKind(sessionKind, taskText);
  const rawSendChat = (currentMessages, { toolsEnabled = true, signal: callSignal, idempotencyKey, onProgress } = {}) => chatFn(
    // HRN2-T01/T02 — bound the prompt every send: cap message count + truncate
    // stale tool-result bodies so long sessions stay within the context window.
    {
      baseUrl,
      model,
      messages: capConversation(editOldToolResults(s.flattenedToolHistoryMode ? flattenToolHistoryForRetry(currentMessages) : currentMessages)), // Wave-D context editing: leituras de rodadas antigas viram stub (s.convo intacto)
      options,
      tools: toolsEnabled ? allToolDefs : undefined,
      // E3 (crash-reconnect) — same key across this turn's retries so the server
      // can dedupe a partial run. Local IPC chatFns simply ignore it.
      idempotency_key: idempotencyKey,
    },
    (delta) => {
      if (signal?.aborted || !delta) return;
      onProgress(); // #3/#8 — QUALQUER chunk do stream reseta o idle-deadline
      if (FORWARDED_DELTAS.has(delta.type)) onEvent(delta); // E2-WS3-T02 — inclui truncamento visível
    },
    // 3º arg (harness-audit E3): AbortSignal. chatFns que falam com a rede
    // (cloud /agent-turn) repassam pro fetch, então o Stop CANCELA a chamada
    // em voo — antes ela rodava até o fim no servidor e ainda era cobrada.
    // chatFns locais sem rede simplesmente ignoram o arg. E4-T01: o callSignal
    // combina o Stop do usuário com o timeout interno (aborta a chamada em voo
    // quando o deadline estoura — não só abandona a Promise).
    callSignal || signal,
  );

  // E4-T01 — wrap each LLM call in a deadline. On timeout: abort the in-flight
  // call (so the network request is actually cancelled) and reject with a tagged
  // timeout error. We link an internal controller to BOTH the timeout and the
  // user's Stop signal so the existing abort path still wins when the user stops.
  const sendChat = (currentMessages, opts = {}) => {
    const controller = new AbortController();
    const onUserAbort = () => controller.abort();
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener('abort', onUserAbort, { once: true });
    }
    let bumpDeadline = () => {}; // #3/#8 — bindBump entrega o arm(); onProgress (cada chunk) o chama
    return callWithTimeout(
      () => rawSendChat(currentMessages, { ...opts, signal: controller.signal, onProgress: () => bumpDeadline() }),
      llmCallTimeoutMs,
      { onTimeout: () => controller.abort(), bindBump: (fn) => { bumpDeadline = fn; }, ttftMs: llmTtftTimeoutMs, maxMs: resolveTimeoutMs(options.llmCallMaxMs, LLM_CALL_MAX_MS) },
    ).finally(() => {
      signal?.removeEventListener?.('abort', onUserAbort);
    });
  };

  // E3 (crash-reconnect) — resilient send. Retries ONLY a thrown network failure
  // (the call never returned → safe to resend), reusing one idempotency key per
  // turn. Never retries a user abort, a hit deadline, or an { ok:false } result
  // (server responded → it ran; resending could double-charge). Surfaces a
  // 'retry' event so the UI can show "reconectando…".
  const sendChatResilient = async (currentMessages, opts = {}) => {
    const idempotencyKey = makeIdempotencyKey();
    let failed = 0;
    for (;;) {
      try {
        return await sendChat(currentMessages, { ...opts, idempotencyKey });
      } catch (e) {
        failed += 1;
        if (signal?.aborted || !isRetryableError(e)) throw e;
        const { retry, delayMs } = resolveRetry(failed);
        if (!retry) throw e;
        onEvent({ type: 'retry', attempt: failed, delayMs, error: e?.message || 'network' });
        await sleep(delayMs, signal);
      }
    }
  };

  const finishFollowupFailed = (error) => {
    onEvent({ type: 'done', content: s.finalText });
    return {
      ok: true,
      finalText: s.finalText,
      iterations: s.iteration,
      stopReason: 'tools_done_followup_failed',
      error: error || undefined,
    };
  };

  const finalizeAfterBudget = async () => {
    const finalPrompt = [
      'Você atingiu o orçamento de ações com ferramentas neste turno.',
      'Não chame mais ferramentas agora.',
      'Responda ao usuário com um resumo objetivo do que você conseguiu descobrir/fazer,',
      'o estado atual, e o próximo passo concreto. Se a tarefa já estiver clara, diga exatamente o que falta.',
      'Não finja que executou algo que não executou.',
      'NÃO invente números como "12 passos" — cite só o que realmente executou com ferramentas.',
    ].join(' ');
    const finalMessages = [...s.convo, { role: 'user', content: finalPrompt }];
    try {
      const reply = await sendChat(finalMessages, { toolsEnabled: false });
      addUsage(usageTotals, reply);
      if (reply?.ok && (reply.content || '').trim()) {
        const split = splitThink(reply.content || '');
        const content = split.content || reply.content || '';
        const reasoning = reply.reasoning || split.reasoning || '';
        applySpineEvent(s, { type: 'set_final_text', text: content });
        onEvent({ type: 'assistant', content, reasoning });
        auditAndFlag(content);
        onEvent({ type: 'done', content });
        return { ok: true, finalText: s.finalText, iterations: s.iteration, stopReason: 'done_after_budget' };
      }
    } catch {
      // Fall through to the explicit max_iterations notice below.
    }
    return null;
  };

  for (; ; applySpineEvent(s, { type: 'tick' })) {
    const boundary = spinePolicy(s, { aborted: !!signal?.aborted, iteration: s.iteration, maxIterations });
    if (boundary.action === 'stop') {
      if (boundary.reason === 'aborted') {
        // Fix 3b — restore last green on abort (best-effort).
        checkpointer.onFail({ signal }).catch(() => {});
        onEvent({ type: 'aborted' });
        return { ok: false, finalText: s.finalText, iterations: s.iteration, stopReason: 'aborted' };
      }
      break;
    }
    onEvent({ type: 'loop_step', n: s.iteration + 1, phase: 'reason' });

    const steer = iterationSteering(s, { readOnly, planMode, isConversationalTask, taskText }); // E15-T04 doom + E14-T03 reminder
    if (steer) { applySpineEvent(s, steer.event); applySpineEvent(s, { type: 'convo_push', message: { role: 'user', content: steer.message } }); }

    let reply;
    try {
      // O 2º arg (onStreamDelta) é OPCIONAL: chatFns com streaming o chamam com
      // { type:'token'|'reasoning', delta } enquanto o modelo gera, pra UI
      // mostrar o progresso ao vivo (nunca mais "em silêncio"). chatFns sem
      // streaming (ex.: cloud) ignoram o arg — o loop segue idêntico ao modo
      // não-stream e os testes existentes continuam válidos.
      reply = await sendChatResilient(s.convo);
    } catch (e) {
      if (signal?.aborted) {
        onEvent({ type: 'aborted' });
        return { ok: false, finalText: s.finalText, iterations: s.iteration, stopReason: 'aborted' };
      }
      if (e?.isTimeout) {
        onEvent({ type: 'loop_stop', stopReason: 'llm_timeout', iterations: s.iteration + 1 });
        return {
          ok: false,
          finalText: s.finalText,
          iterations: s.iteration + 1,
          stopReason: 'llm_timeout',
          llmCallTimeoutMs,
          wallClockMs: e.wallClockMs, // WSX — presente só quando o teto ABSOLUTO estourou (e é truthy no ramo isTimeout)
          maxIterations,
        };
      }
      if (isContextOverflow(e?.message)) {
        onEvent({ type: 'error', error: e?.message || 'context overflow' });
        return { ok: false, finalText: s.finalText, iterations: s.iteration, stopReason: 'context_overflow' };
      }
      if (s.executedAnyTool && !s.flattenedToolHistoryMode) {
        applySpineEvent(s, { type: 'flatten_mode' });
        try {
          reply = await sendChat(s.convo);
        } catch (retryError) {
          return finishFollowupFailed(retryError?.message || String(retryError));
        }
      } else if (s.executedAnyTool) {
        return finishFollowupFailed(e?.message || String(e));
      } else {
        onEvent({ type: 'error', error: e?.message || String(e) });
        return { ok: false, finalText: s.finalText, iterations: s.iteration, stopReason: 'exception' };
      }
    }

    if (!reply?.ok && s.executedAnyTool && !s.flattenedToolHistoryMode && !isContextOverflow(reply?.error)) {
      applySpineEvent(s, { type: 'flatten_mode' });
      try {
        reply = await sendChat(s.convo);
      } catch (retryError) {
        return finishFollowupFailed(retryError?.message || String(retryError));
      }
    }

    if (!reply?.ok && s.executedAnyTool && !isContextOverflow(reply?.error)) {
      return finishFollowupFailed(reply?.error || 'modelo não respondeu');
    }

    if (reply?.ok && s.flattenedToolHistoryMode) {
      applySpineEvent(s, { type: 'convo_replace', convo: flattenToolHistoryForRetry(s.convo) });
    }

    if (signal?.aborted) {
      onEvent({ type: 'aborted' });
      return { ok: false, finalText: s.finalText, iterations: s.iteration, stopReason: 'aborted' };
    }

      if (!reply?.ok) {
      if (isContextOverflow(reply?.error)) {
        onEvent({ type: 'error', error: reply.error });
        return { ok: false, finalText: s.finalText, iterations: s.iteration, stopReason: 'context_overflow' };
      }
      if (s.executedAnyTool) {
        return finishFollowupFailed(reply?.error || 'modelo não respondeu');
      }
      const errMsg = reply?.error || 'modelo não respondeu';
      onEvent({ type: 'error', error: errMsg });
      return {
        ok: false,
        finalText: s.finalText,
        iterations: s.iteration,
        stopReason: 'model_error',
        error: errMsg,
        maxIterations,
      };
    }

    addUsage(usageTotals, reply); // E2-WS2-T01 — usage 1x no ponto de commit do reply (retries não duplicam)
    const overBudget = budgetExceeded(usageTotals, model, { maxCostUsd, maxTokensOut }); // Wave-J hard-stop
    if (overBudget) { onEvent({ type: 'error', error: `Hard-stop de orçamento: ${overBudget.reason}. O trabalho parcial está no worktree/progress.` }); return { ok: false, finalText: s.finalText, iterations: s.iteration, stopReason: 'budget_exceeded', error: overBudget.reason, maxIterations }; }

    // Extrai <think> inline do content (cloud DeepSeek/MiniMax) → reasoning +
    // texto limpo. Modelos com campo `reasoning` separado (LM Studio #78) já vêm
    // prontos; o split só age quando há tags. A history re-enviada usa o content
    // LIMPO (sem tags) — mais saudável pro próximo turno.
    const split = splitThink(reply.content || '');
    let content = split.content;
    const reasoning = reply.reasoning || split.reasoning || '';
    const toolCalls = normalizeLoopToolCalls(reply, s.iteration); // extraído p/ loop/ (dp-loc, Wave-B)
    // Wave-B — despacho de finish_reason: truncamento de TEXTO ganha 1 continuação bounded;
    // filtro de conteúdo vira stop honesto; 2º corte anexa nota de entrega parcial.
    const finAct = resolveFinishAction(reply, toolCalls.length, !!s.truncationContinued);
    if (finAct?.kind === 'continue') { s.truncationContinued = true; applySpineEvent(s, { type: 'convo_push', message: { role: 'assistant', content } }); applySpineEvent(s, { type: 'convo_push', message: { role: 'user', content: finAct.nudge } }); continue; }
    if (finAct?.kind === 'filtered') { onEvent({ type: 'error', error: finAct.error }); return { ok: false, finalText: content, iterations: s.iteration, stopReason: 'content_filtered', error: finAct.error }; }
    if (finAct?.kind === 'note') content += finAct.note;
    // arch-fable5 A2 — remember each call's name so the honest-progress auditor can match
    // a status claim to the tool result it (should) be backed by.
    for (const tc of toolCalls) applySpineEvent(s, { type: 'tool_name', id: tc.id, name: tc.name, args: tc.arguments });

    if (content || reasoning) {
      if (content) { applySpineEvent(s, { type: 'set_final_text', text: content }); auditAndFlag(content); }
      onEvent({ type: 'assistant', content, reasoning });
    }

    // Anti-loop guard: same non-empty TEXT repeated MAX_IDENTICAL_CONTENT turns in
    // a row WITH NO tool calls → the model is stuck emitting the same line forever
    // (the "Use tool. End." x200 meltdown). Gated to tool-LESS turns so legitimate
    // controlled repetition that DOES act (e.g. the autocorrect fix→red→fix cap, the
    // tool-repeat guard) is untouched. A productive tool turn resets the streak.
    const csig = contentSig(content);
    applySpineEvent(s, { type: 'content_check', sig: csig, noTools: toolCalls.length === 0 && !!csig });
    if (toolCalls.length === 0 && csig && identicalTripped(s.identicalContentRun)) {
      onEvent({ type: 'error', error: `Loop detectado: o modelo repetiu a mesma resposta ${s.identicalContentRun}× sem agir — turno abortado.` });
      return { ok: false, finalText: s.finalText, iterations: s.iteration + 1, stopReason: 'identical_output_loop', maxIterations };
    }

    // Sem tool calls → só termina se já agiu de verdade; plano/texto puro = nudge.
    if (toolCalls.length === 0) {
      // FC-2 — early decisions delegated to the pure classifyToollessReply module
      // (plan/conversational/nudge). Late decisions (audit/oracle/deliverable/
      // green-claim/done) stay inline — they depend on impure ops (deliverableAudit,
      // runOracle) and sequential fall-through. Same predicates, same order (L-1).
      const replyAction = classifyToollessReply(s, content, {
        planMode, isConversationalTask, readOnly, fastGenerative, doneClaimBlockOn,
        maxStallNudges: MAX_STALL_NUDGES, claimsUnbackedGreen, claimsUnbackedWrite,
      });
      // B-6 — os 6 handlers de replyAction saíram pro loop/applyToollessReply
      // (dp-loc do spine); eventos/ordem idênticos, o spine obedece o directive.
      const rd = applyToollessReply(replyAction, {
        s, content, onEvent, applySpineEvent, maxIterations,
        nudges: { presentPlan: PRESENT_PLAN_NUDGE, toolBeforeClaim: TOOL_BEFORE_CLAIM_NUDGE },
      });
      if (rd.control === 'continue') continue;
      if (rd.control === 'return') return rd.value;
      // E3 — deterministic deliverable audit (contrast/broken-link) on the web
      // files written this turn. One-shot: if it finds error-severity problems,
      // nudge the agent to FIX before finishing. Bounded (auditNudged) so a model
      // that can't fix can never loop. No web files / clean audit ⇒ finish normally.
      if (!s.auditNudged && s.writtenFiles.some((f) => /\.(html?|css)$/i.test(f.path))) {
        applySpineEvent(s, { type: 'nudge', which: 'audit' });
        const errs = deliverableAudit({ files: s.writtenFiles, existingPaths: s.knownExistingPaths }).findings.filter((f) => f.severity === 'error');
        if (errs.length) {
          const lines = errs.slice(0, 8).map((f) => `- ${f.file}: ${f.message}`);
          onEvent({ type: 'quality_audit', findings: errs });
          applySpineEvent(s, { type: 'convo_push', message: {
            role: 'user',
            content: [
              '[NUDGE] Antes de concluir: a verificação do entregável reprovou no padrão de qualidade (WCAG AA / links).',
              'CORRIJA com apply_edit/write_file e só então conclua — não responda texto, aja:',
              ...lines,
            ].join('\n'),
          } });
          continue;
        }
      }
      // E1 (self-improve-hardening) — AUTO_CORRECT: the verify gate WITH TEETH. When
      // the agent edited code/UI files (.jsx/.tsx/.css/.js) the ORACLE runs the
      // available verifiers (executable verifiers are the JUDGE) and BLOCKS DONE on a
      // red verdict, nudging a FIX up to AUTO_CORRECT_CAP; the next red ABORTs. The
      // default verifier is the live-preview console (the #297 metric, now a real
      // CONSUMER); callers inject more (types/lint/smoke/build) +/− Google triage via
      // cfg.autoCorrect. No verifier (no preview / no-window) ⇒ SILENT no-op — honest,
      // never a false block; preserves the prior no-window behavior exactly.
      // P0-T06 — generative fast mode: a creative one-shot build skips the
      // AUTO_CORRECT RED→re-run gauntlet (the speed cost). Emit one transparency
      // event so the skip is honest (the user sees the fast path was taken).
      if (fastGenerative && !s.generativeFastAnnounced
          && s.writtenFiles.some((f) => /\.(jsx?|tsx?|css)$/i.test(f.path))) {
        applySpineEvent(s, { type: 'generative_fast_announced' });
        onEvent({ type: 'generative_fast', skipped: 'auto_correct' });
      }
      if (!s.autoCorrectGreen && !fastGenerative && s.writtenFiles.some((f) => /\.(jsx?|tsx?|css)$/i.test(f.path))) {
        const verifiers = (autoCorrect && autoCorrect.verifiers)
          ? autoCorrect.verifiers
          : (typeof window !== 'undefined' && window.apolo?.preview?.console
            ? { console: previewConsoleVerifier(window.apolo.preview) }
            : {});
        const triage = autoCorrect ? autoCorrect.triage : null;
        if (Object.keys(verifiers).length === 0) {
          applySpineEvent(s, { type: 'auto_correct_noop' });
        } else {
          const oracleResult = await runOracle({ verifiers, triage, taskText, signal });
          const plan = planOracleStep(oracleResult, { attempts: s.autoCorrectAttempts, cap: AUTO_CORRECT_CAP });
          if (plan.outcome === 'green') {
            markStage(s.turnTrace, 'verify', 'ok', `oracle green (${plan.greenVerifiers})`);
            applySpineEvent(s, { type: 'auto_correct_green', result: plan.autoCorrectResult });
            onEvent({ type: 'auto_correct', result: s.autoCorrectResult });
            // Fix 3b — capture checkpoint on green (best-effort).
            checkpointer.onGreen({ verdict: oracleResult, taskId: 'loop-turn', signal }).catch(() => {});
          } else {
            markStage(s.turnTrace, 'verify', 'error', plan.failedVerifiers);
            onEvent({ type: 'quality_audit', source: 'oracle', findings: plan.findings });
            if (plan.outcome === 'abort') {
              applySpineEvent(s, { type: 'auto_correct_green', result: plan.autoCorrectResult });
              onEvent({ type: 'auto_correct', result: s.autoCorrectResult });
              onEvent({ type: 'loop_stop', stopReason: plan.stopReason, iterations: s.iteration + 1 });
              return { ok: false, finalText: s.finalText, iterations: s.iteration + 1, stopReason: plan.stopReason, autoCorrect: s.autoCorrectResult, maxIterations };
            }
            applySpineEvent(s, { type: 'auto_correct_attempt' });
            applySpineEvent(s, { type: 'convo_push', message: { role: 'user', content: plan.fixNudge } });
            continue;
          }
        }
      }
      // P3-T02 — no SILENT green for non-code deliverables. The code oracle above
      // only runs for .js/.jsx/.ts/.tsx/.css; a .md/.json/.sql/.yml/.py deliverable
      // would otherwise finish as a silent pass. When a turn wrote files but the code
      // oracle did NOT verify them green, apply the cheap APPLICABLE check we have
      // (.json must parse → a real defect if not) and HONESTLY LABEL whatever has no
      // applicable check as `unverified` (carried on the result + an event), instead
      // of a silent green. Additive: the `done` verdict is unchanged (L-3) — only the
      // honesty signal is added. (.html/.css already went through deliverableAudit.)
      let unverifiedDeliverable;
      if (s.writtenFiles.length && !reachedHonestGreen(s, readGreenLedgerEnforced())) {
        const nonCode = s.writtenFiles.filter((f) => !/\.(jsx?|tsx?|css)$/i.test(f.path));
        const deliverableFindings = [];
        const unverified = [];
        for (const f of nonCode) {
          if (/\.json$/i.test(f.path)) {
            try { JSON.parse(String(f.content ?? '')); }
            catch (e) { deliverableFindings.push({ file: f.path, severity: 'error', message: `JSON inválido: ${e?.message || 'parse error'}` }); }
          } else if (!/\.(html?|css)$/i.test(f.path)) {
            unverified.push(f.path);
          }
        }
        if (deliverableFindings.length) onEvent({ type: 'quality_audit', source: 'deliverable', findings: deliverableFindings });
        if (unverified.length) {
          unverifiedDeliverable = unverified;
          onEvent({ type: 'unverified_deliverable', files: unverified });
        }
      }
      // P3-T01 — honest-green for the DONE narration (the BLOCKING half of A2). If
      // the model is about to finish while claiming tests/build PASS ('green') with
      // NO tool result backing it AND no oracle verifier ran green this turn
      // (autoCorrectResult is null/!reachedGreen), that `done` is a fabricated
      // success. Nudge ONCE to actually verify or retract; if the lie persists, BLOCK
      // with stopReason 'unbacked_green_claim'. SURGICAL: a done with no green claim,
      // a backed one, or an oracle-verified one never reaches here (success path
      // unchanged, L-3). Gated default-ON (apolo_done_claim_block).
      if (
        doneClaimBlockOn
        && !reachedHonestGreen(s, readGreenLedgerEnforced())
        && claimsUnbackedGreen(content)
      ) {
        if (!s.greenClaimNudged) {
          applySpineEvent(s, { type: 'nudge', which: 'greenClaim' });
          // No honest_progress_flag here — that event belongs to the advisory
          // auditAndFlag (the apolo_progress_audit flag). The block's own signal is
          // the nudge then the loop_stop below, keeping the two flags decoupled.
          applySpineEvent(s, { type: 'convo_push', message: { role: 'user', content: UNBACKED_GREEN_NUDGE } });
          continue;
        }
        onEvent({ type: 'loop_stop', stopReason: 'unbacked_green_claim', iterations: s.iteration + 1 });
        return { ok: false, finalText: s.finalText, iterations: s.iteration + 1, stopReason: 'unbacked_green_claim', maxIterations };
      }
      if (ranChecksGate(s)) {
        applySpineEvent(s, { type: 'nudge', which: 'ranChecks' });
        applySpineEvent(s, { type: 'convo_push', message: { role: 'user', content: RAN_CHECKS_NUDGE } });
        continue;
      }
      // E2 — per-turn observability: trace + RAG quality at completion.
      markStage(s.turnTrace, 'model', 'ok', `${s.iteration + 1} iter`);
      const traceSummary = summarizeTrace(s.turnTrace);
      onEvent({ type: 'turn_trace', trace: traceSummary });
      // Compute RAG quality ONCE; carry on result for telemetry.
      const ragQuality = s.ragChunks.length
        ? scoreRagInjection({ injectedChunks: s.ragChunks, finalText: s.finalText })
        : null;
      if (ragQuality) onEvent({ type: 'rag_quality', ...ragQuality });
      // E8 — stop lifecycle hook at turn completion (observe; flag OFF ⇒ no-op).
      await stopHook(s.finalText);
      onEvent({ type: 'done', content });
      return { ok: true, finalText: s.finalText, iterations: s.iteration + 1, stopReason: 'done', trace: traceSummary, ragQuality, autoCorrect: s.autoCorrectResult, unverifiedDeliverable };
    }

    // Context-first safety net (one-shot): on a COMPLEX/multi-file task, if the
    // model's FIRST tool action is a write/edit with ZERO prior context-gathering
    // (no semantic_search/search_brain/grep/read this turn or earlier), route it
    // to gather context FIRST. This is "plan-first via TOOLS", NOT a prose-plan
    // instruction (which would regress audit-mode). Fires at most once; never on
    // trivial tasks (isComplexTask gates it). The write is deferred — the model
    // re-plans with context on the next turn.
    if (isComplexTask && !s.gatheredContext && !s.contextNudged) {
      const idxFirstWrite = toolCalls.findIndex((tc) => WRITE_TOOLS.has(tc.name));
      const idxFirstContext = toolCalls.findIndex((tc) => CONTEXT_TOOLS.has(tc.name));
      const writeBeforeContext = idxFirstWrite !== -1
        && (idxFirstContext === -1 || idxFirstWrite < idxFirstContext);
      if (writeBeforeContext) {
        applySpineEvent(s, { type: 'nudge', which: 'context' });
        applySpineEvent(s, { type: 'convo_push', message: { role: 'user', content: CONTEXT_FIRST_NUDGE } });
        continue;
      }
    }

    // Registra a assistant turn com os tool_calls no histórico (formato API).
    applySpineEvent(s, { type: 'convo_push', message: {
      role: 'assistant',
      content,
      tool_calls: toolCalls.map(toApiToolCall),
    } });

    // Plan Mode v2 — TERMINAL tool (present_plan). When the model surfaces the plan
    // via the structured tool, the turn is OVER (analogous to "no tool calls"): we
    // do NOT keep iterating. Emit the tool_call + tool_result so the transcript stays
    // consistent, then carry the parsed plan on the result (agentResult.plan) so the
    // renderer can show a proposal card. If the model bundled other calls, present_plan
    // still wins (the directive says call it alone); a malformed/empty plan returns an
    // actionable error and re-prompts instead of terminating with a blank card.
    const terminalTc = toolCalls.find((tc) => TERMINAL_TOOLS.has(tc.name));
    if (terminalTc && terminalTc.name === 'present_plan') {
      const repairedArgs = repairToolCall(terminalTc.name, terminalTc.arguments, { taskText, root });
      onEvent({ type: 'tool_call', id: terminalTc.id, name: terminalTc.name, args: repairedArgs, repaired: false });
      const parsedPlan = parsePresentPlan(repairedArgs);
      if (!parsedPlan.ok) {
        const errMsg = `Erro: present_plan ${parsedPlan.error}. Reenvie com summary (1-2 frases) e steps:[{"n":1,"action":"…"}].`;
        onEvent({ type: 'tool_result', id: terminalTc.id, name: terminalTc.name, result: errMsg, approved: false });
        applySpineEvent(s, { type: 'convo_push', message: { role: 'tool', tool_call_id: terminalTc.id, content: errMsg } });
        continue; // let the model re-emit a valid plan
      }
      const result = await runTool('present_plan', repairedArgs, { root, signal, sessionId });
      onEvent({ type: 'tool_result', id: terminalTc.id, name: terminalTc.name, result, approved: true });
      applySpineEvent(s, { type: 'convo_push', message: { role: 'tool', tool_call_id: terminalTc.id, content: result } });
      onEvent({ type: 'plan', plan: parsedPlan.plan });
      onEvent({ type: 'done', content: s.finalText });
      return {
        ok: true,
        finalText: s.finalText,
        iterations: s.iteration + 1,
        stopReason: 'plan_presented',
        plan: parsedPlan.plan,
        maxIterations,
      };
    }

    // wave8 — lote 100% read-only executa em PARALELO (gate roda ANTES do prewarm; regras no módulo testado).
    const prewarm = prewarmParallelReads(toolCalls, extraByName, { gateDenies: (tc) => evaluateToolGate(tc.name, repairToolCall(tc.name, tc.arguments, { taskText, root }), { toolPolicy, root, guardrailEnabled: readToolGuardrailEnabled(), correctionMode: s.autoCorrectAttempts > 0 }).denied, execute: (tc) => { const a = repairToolCall(tc.name, tc.arguments, { taskText, root }); const ex = extraByName.get(tc.name); return ex ? ex.execute(a, { root, signal, sessionId, attachImages: () => {} }) : runTool(tc.name, a, { root, signal, sessionId }); } });
    // E1-WS1-T04 (live-verify) — user msgs (vision/nudges) adiadas até o fim do lote multi-call (loop/batchBuffer).
    const batchBuf = createBatchBuffer({ batchSize: toolCalls.length, enabled: readNativeAnthropicToolsEnabled(), push: (m) => applySpineEvent(s, { type: 'convo_push', message: m }) });
    for (const [ti, tc] of toolCalls.entries()) {
      if (signal?.aborted) {
        const notRun = 'Não executado: turno abortado pelo usuário (Stop).'; for (const rem of toolCalls.slice(ti)) { onEvent({ type: 'tool_result', id: rem.id, name: rem.name, result: notRun, approved: false }); applySpineEvent(s, { type: 'convo_push', message: { role: 'tool', tool_call_id: rem.id, content: notRun } }); } // Wave-B doutrina Claude: call NÃO executado ainda recebe tool_result (sem tool_calls órfãos)
        onEvent({ type: 'aborted' });
        return { ok: false, finalText: s.finalText, iterations: s.iteration, stopReason: 'aborted' };
      }

      // E1-WS3 + WS-E — truncated/unparseable tool args: don't execute; surface the cause
      // (+ nudge), and ESCALATE to 'output_truncated' on the 2nd repeat. Same {control}
      // shape as applyToollessReply → reuses the spine dispatch. loop/argsErrorGate.js.
      const argsErr = handleArgsError(tc, s, { onEvent, applySpineEvent });
      if (argsErr) { if (argsErr.control === 'return') return argsErr.value; continue; }

      const repairedArgs = repairToolCall(tc.name, tc.arguments, { taskText, root });
      onEvent({
        type: 'tool_call',
        id: tc.id,
        name: tc.name,
        args: repairedArgs,
        repaired: JSON.stringify(parseToolArgs(tc.arguments)) !== JSON.stringify(repairedArgs),
      });

      // E8 (super-marathon) — pre_tool_call lifecycle hook (loop/hookLifecycle.js). Flag
      // OFF ⇒ returns null (no fireHooks call, byte-identical). A blocking hook adds a
      // deny into the gate below; commandSafety still applies downstream (defense-in-depth).
      const preToolHookBlock = await preToolHookGate(tc.name, repairedArgs);
      // Camada V + Fix 3 (code-tab-to-10) — policy + file-deletion/external-write guardrail,
      // composed PURELY in loop/toolGate.js. policy null = no-op; guardrail OFF = byte-identical.
      const pol = evaluateToolGate(tc.name, repairedArgs, { toolPolicy, root, guardrailEnabled: readToolGuardrailEnabled(), preToolHookBlock, correctionMode: s.autoCorrectAttempts > 0 }); // E15-T05

      // PR9a strangler stage 1 — the PURE short-circuit cluster (read-only deny →
      // toolGate deny → send_to_user delivery) is decided in loop/decideToolDispatch.js
      // and APPLIED here. Order preserved; goldens prove byte-identical behavior.
      const dispatch = decideToolDispatch({
        tc,
        repairedArgs,
        readOnly,
        isMutating: MUTATING_TOOLS.has(tc.name) || extraByName.get(tc.name)?.mutating,
        toolGate: pol,
        sendToUserOn,
        isExcluded: excludedToolNames.has(tc.name), // fix A — def filtrado; nome alucinado morre aqui
      });
      if (dispatch.action !== 'proceed') {
        for (const e of dispatch.events) onEvent(e);
        for (const se of dispatch.spineEvents) applySpineEvent(s, se);
        continue;
      }

      // S4 (E4) — auto-review classifier: pre-tool gate governed by .apollo/permissions.json.
      // INTERMEDIATE signal only (honest-green stays the FINAL verdict — this gate never
      // decides task success/failure; it only allows/blocks/escalates THIS tool call).
      // Read-only tools are auto-allowed; destructive-without-allow is denied-by-default.
      let reviewDecision = null;
      if (autoReviewActive) {
        const review = await runAutoReview(
          { name: tc.name, arguments: repairedArgs },
          { root, sessionId },
          autoReview.policy,
          autoReview.opts || {},
        );
        reviewDecision = review.decision;
        recordDecision(autoReviewMetrics, review.decision); // local telemetry (counts only, no PII)
        onEvent({ type: 'auto_review', id: tc.id, name: tc.name, decision: review.decision, reason: review.reason });
        if (review.decision === 'block') {
          // Feedback loop: the explanation goes back to the agent so it picks another path.
          const blocked = `Bloqueado pelo auto-review: ${review.reason}. Escolha outra abordagem ou ajuste .apolo/permissions.json.`;
          onEvent({ type: 'tool_result', id: tc.id, name: tc.name, result: blocked, approved: false });
          applySpineEvent(s, { type: 'convo_push', message: { role: 'tool', tool_call_id: tc.id, content: blocked } });
          continue;
        }
        // 'ask' → fall through to the existing human approval gate.
        // 'allow' → may skip the prompt (explicit policy auto-run), handled below.
      }

      reviewDecision = await autonomyGateStep(tc, repairedArgs, reviewDecision, extraByName, onEvent); // E3 autonomy gate (flag apolo_so_autonomy; loop/autonomyStep.js)
      let approved = true;
      // Gate de aprovação em UMA decisão PURA (loop/shipApproval.js): 'allow' do
      // auto-review auto-executa; 'ask'+mutante pergunta; e o guard do incidente
      // PR #618 — comando de PUBLICAÇÃO (git push / gh pr) em sessão de CHAT
      // SEMPRE pergunta, mesmo com Auto-aprovar/permissionsMode 'ignore'.
      const approvalGate = decideApprovalGate({ sessionKind, permissionsMode, reviewDecision, name: tc.name, args: repairedArgs, isMutating: MUTATING_TOOLS.has(tc.name) || extraByName.get(tc.name)?.mutating });
      if (approvalGate === 'ask') {
        try {
          approved = !!(await requestApproval?.(describeToolCall(tc.name, repairedArgs))); // !! — sem dialog plugado, 'ask' nega
        } catch {
          approved = false;
        }
      }

      let result;
      let repeatWarned = false; // E3-T03 — o Aviso de repetição NÃO é erro de tool
      const imgSink = []; // vision hook: tools push image data-URLs via ctx.attachImages
      if (!approved) {
        result = 'Usuário NEGOU a execução desta ação. Não tente de novo sem confirmar com o usuário.';
      } else {
        const sig = normalizedToolSignature(tc.name, repairedArgs);
        applySpineEvent(s, { type: 'tool_repeat', sig });
        const seen = s.toolRepeatCounts.get(sig);
        if (seen > 2) {
          repeatWarned = true;
          result = [
            `Aviso: a ferramenta ${tc.name} com os mesmos argumentos já foi executada ${seen - 1} vezes neste turno.`,
            'Não repita a mesma ação. Use os resultados já recebidos, escolha uma ferramenta diferente ou conclua a tarefa com o que já sabe.',
          ].join('\n');
        } else {
          // HRN1-T02 — thread the loop's AbortSignal so Stop kills an in-flight
          // run_command on the host (not just the cloud call).
          const extra = extraByName.get(tc.name);
          result = prewarm?.has(tc.id) ? await prewarm.get(tc.id) // wave8 — lote read-only já executou em paralelo
            : extra ? await extra.execute(repairedArgs, { root, signal, sessionId, attachImages: (urls) => pushImages(imgSink, urls) })
              : await runTool(tc.name, repairedArgs, { root, signal, sessionId });
          result = rootlessChatToolResult(chatWithoutRoot, result); // E7-T09
          applySpineEvent(s, { type: 'tool_executed' });
          // P4-T05 — apply the PURE effect descriptor (extracted: deriveToolEffects).
          // P4-T03 — the ordered effect application is a pure step (applyToolEffects):
          // arrays mutate in place (passed by ref); scalars mutate on the acc + write
          // back; the markStage telemetry is returned and applied here. Order identical.
          const eff = deriveToolEffects(tc.name, repairedArgs, result, resultIsError(result));
          const toolAcc = { gatheredContext: s.gatheredContext, ragChunks: s.ragChunks, wroteFile: s.wroteFile, writtenFiles: s.writtenFiles, knownExistingPaths: s.knownExistingPaths, committed: s.committed };
          const mark = applyToolEffects(eff, toolAcc);
          s.gatheredContext = toolAcc.gatheredContext;
          s.wroteFile = toolAcc.wroteFile;
          s.committed = toolAcc.committed;
          // #6 — sem isto o recordEdit do greenLedger era dead code (o gate isStale nunca dispara).
          if (eff.wroteFile) recordEdit(s.greenLedger, eff.wroteFile.path);
          if (mark) markStage(s.turnTrace, mark.stage, mark.status, tc.name);
        }
      }

      onEvent({ type: 'tool_result', id: tc.id, name: tc.name, result, approved });
      applySpineEvent(s, { type: 'convo_push', message: { role: 'tool', tool_call_id: tc.id, content: result } });
      const imgMsg = imageMessage(tc.name, imgSink); // vision: feed attached images to the model
      if (imgMsg) batchBuf.add(imgMsg);
      // E8 — post_tool_call lifecycle hook (observe; flag OFF ⇒ no-op, byte-identical).
      await postToolHook(tc.name, repairedArgs, result);

      if (isMissingArgsError(result) && !s.toolFailureNudged) {
        applySpineEvent(s, { type: 'nudge', which: 'toolFailure' });
        batchBuf.add({ role: 'user', content: buildMissingArgsNudge(tc.name, taskText) || TOOL_FAILURE_NUDGE });
      }

      if (isConfigToolError(result)) {
        onEvent({ type: 'loop_stop', stopReason: 'no_root', iterations: s.iteration + 1 });
        return {
          ok: false,
          finalText: s.finalText,
          iterations: s.iteration + 1,
          stopReason: 'no_root',
          maxIterations,
        };
      }
      // E3-T03 — pós-execução consolidada (loop/loopHealth.toolOutcomeStop):
      // repeat-warning tem contador+stopReason PRÓPRIOS ('tool_repeat_loop'), erro
      // real alimenta o spiral, sucesso reseta. Antes o "Aviso:" de repetição contava
      // como erro e o run morria como 'tool_failure_spiral' (motivo enganoso).
      const healthStop = toolOutcomeStop(s, repeatWarned, result, tc.name); // D-11: erro por tool
      // E12-T03 — ambiente quebrado NÃO é terminal: injeta o nudge de reparo e segue.
      if (healthStop === 'env_repair_nudge') {
        batchBuf.add({ role: 'user', content: ENV_REPAIR_NUDGE });
      } else if (healthStop) {
        const repeatInfo = buildRepeatInfo(healthStop, tc.name, summarizeToolArgs(repairedArgs), s);
        onEvent({ type: 'loop_stop', stopReason: healthStop, iterations: s.iteration + 1, repeatInfo });
        return { ok: false, finalText: s.finalText, iterations: s.iteration + 1, stopReason: healthStop, repeatInfo, maxIterations };
      }
    }
    batchBuf.flush(); // E1-WS1-T04 — user msgs adiadas aterrissam DEPOIS do último tool_result do lote
  }

  const finalized = await finalizeAfterBudget();
  if (finalized) return { ...finalized, maxIterations };
  onEvent({ type: 'max_iterations', max: maxIterations });
  return { ok: true, finalText: s.finalText, iterations: s.iteration, stopReason: 'max_iterations', maxIterations };
}
